import { useCallback, useEffect, useMemo } from 'react'
import {
  faLock,
  faUsers,
  faUniversity,
  faBook,
  faSuitcase,
  faUser,
  faHome,
  faQuestionCircle,
  faEnvelope,
  faCompass,
  faChartMixed,
  faPiggyBank,
  faGift,
  faMoneyBillTransfer,
  faFile,
  faCalculator,
  faComment,
  faBank,
  faHandHoldingCircleDollar,
  faUserMagnifyingGlass,
} from '@fortawesome/pro-regular-svg-icons'
import { isNil } from 'lodash'
import { useLocation } from 'react-router-dom'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

import { SidebarConfigProps } from '@/reducers/sidebar.slice'
import { MASS_MESSAGE_ADMIN, QA_PAGE_ENABLED } from '@/utils/adminHelpers'
import {
  getCurrentUser,
  getUserIsAdmin,
  isCanceled,
  selectIsInUnservicedFreeTrial,
  isEmailVerified,
} from '@/selectors/user.selectors'
import { selectYourTurnNumber } from '@/components/conversations/conversation.slice'
import { fetchUnreadMessageCount } from '@/components/conversations/actions'
import {
  fetchPayrollCompany,
  fetchPayrollProfileIfNeeded,
} from '@/features/payroll/payrollActions'
import { filterNulls, useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { fetchSubscriptions } from '@/reducers/subscription.slice'
import { fetchBankingProfile } from '@/features/banking/bankingSlice'
import { selectBankingProfile } from '@/v2-features/banking/banking.selectors'
import { FEATURE_FLAG_KEYS } from '@/flags'
import {
  FETCH_ALL_ANNUAL_TAX_DETAILS_KEY,
  fetchAllAnnualTaxDetailsIfNeeded,
} from '@/features/admin/annual-tax-details/annualTaxDetails.slice'
import {
  FETCH_ANNUAL_TAX_FILING_FORMS_KEY,
  fetchAnnualTaxFilingFormsIfNeeded,
} from '@/features/taxes/annual-taxes/annualTaxFilingForms.slice'
import {
  FETCH_TAX_FILINGS_KEY,
  fetchAnnualTaxFilingsIfNeeded,
} from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import { getIsFetching, getIsFetchingOrNotStarted } from '@/reducers/fetch'
import { selectFinancialAdvisoryProfile } from '@/features/financial-advisory/financialAdvisory.selectors'
import { FinancialAdvisoryProfilesStatus } from '@/features/financial-advisory/financialAdvisory.slice'
import { userHasFinancialAdvisoryAccess } from '@/features/financial-advisory/service'
import { defaultMessagesURL } from '@/features/zendesk/helpers'
import { isAdviserHiddenRoute } from '@/features/adviser-chat/lib/hiddenRoutes'
import {
  HAVEN_CONTEXT_QUERY_PARAM,
  HAVEN_ENTRY_QUERY_PARAM,
  HAVEN_VIEW_QUERY_PARAM,
  OPEN_HAVEN_QUERY_PARAM,
} from '@/features/adviser-chat/lib/havenRoutes'
import { RAIL_FILTERS } from '@/features/adviser-chat/lib/constants'
import {
  HAVEN_ENTRY_POINT,
  HAVEN_LAUNCH_ACTION,
} from '@/features/adviser-chat/lib/types'
import { fetchUserYearEndModuleStatuses } from '@/features/year-end-module-status/yearEndModuleStatus.slice'
import { selectPayrollNavConfig } from './navigation.selectors'
import NavigationCountIconRestructured from './NavigationCountIconRestructured'
import { useRouteMapper } from '@/components/routes/useRouteMapper'
import {
  hasPermissionsForRoute,
  selectUserHasPermission,
  selectUserPermissions,
} from '@/reducers/auth/permission.selectors'
import {
  FETCH_USER_PERMISSIONS_KEY,
  ProductFeatureId,
} from '@/actions/permissionActions'

export type SidebarConfig = Record<string, SidebarConfigProps>

export const useSidebarConfig = (): SidebarConfig | null => {
  const dispatch = useAppDispatch()
  const isAdmin = useReselector(getUserIsAdmin)
  const user = useReselector(getCurrentUser)
  const userEmailVerified = useReselector(isEmailVerified)
  const messageCount = useReselector(selectYourTurnNumber)
  const payrollNavConfig = useReselector(selectPayrollNavConfig)
  const { ALL_ROUTES } = useRouteMapper()
  const { pathname, search } = useLocation()
  const userPermissions = useReselector(selectUserPermissions)
  const isFetchingPermissions = useReselector(
    getIsFetching,
    FETCH_USER_PERMISSIONS_KEY
  )
  const fetchingAnnualTaxDetails = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_ALL_ANNUAL_TAX_DETAILS_KEY
  )

  const fetchingAnnualTaxFilings = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_TAX_FILINGS_KEY
  )

  const fetchingAnnualTaxFilingForms = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_ANNUAL_TAX_FILING_FORMS_KEY
  )

  const annualTaxFormsLoading =
    fetchingAnnualTaxDetails ||
    fetchingAnnualTaxFilings ||
    fetchingAnnualTaxFilingForms

  const designSystem2Enabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableDesignSystem2,
    false
  )

  const showReferralsLink = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.referralExperienceEnabled,
    false
  )

  const havenMessagesViewEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.havenMessagesView,
    false
  )
  const havenHandToSupportEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.havenHandToSupport,
    false
  )

  const bankingProfile = useReselector(selectBankingProfile)
  const hasBankingProfile = Boolean(bankingProfile)

  const getConfigWithPermissions = useCallback(
    (config: SidebarConfig) => {
      if (!isFetchingPermissions && userPermissions) {
        Object.entries(config).forEach(([key, c]) => {
          if (c.to) {
            const hasPermission = hasPermissionsForRoute(
              userPermissions,
              ALL_ROUTES,
              c.to
            )
            config[key] = {
              ...c,
              icon: hasPermission ? c.icon : faLock,
            }
          }
          if (c.pages) {
            c.pages.forEach((page) => {
              if (page.to) {
                const hasPermission = hasPermissionsForRoute(
                  userPermissions,
                  ALL_ROUTES,
                  page.to
                )
                page.icon = hasPermission ? page.icon : faLock
              }
            })
          }
        })
      }
      return config
    },
    [isFetchingPermissions, userPermissions, ALL_ROUTES]
  )

  useEffect(() => {
    if (user?.id && !isAdmin) {
      dispatch(fetchAllAnnualTaxDetailsIfNeeded())
      dispatch(fetchAnnualTaxFilingsIfNeeded())
      dispatch(fetchAnnualTaxFilingFormsIfNeeded())
      dispatch(fetchPayrollCompany())
      dispatch(fetchPayrollProfileIfNeeded())
      dispatch(fetchSubscriptions())
      dispatch(fetchUnreadMessageCount())
      dispatch(fetchUserYearEndModuleStatuses())
    }
  }, [user?.id, dispatch, isAdmin])

  useEffect(() => {
    if (user?.id && !isAdmin) {
      dispatch(fetchBankingProfile())
    }
  }, [user?.id, dispatch, isAdmin])

  const hasQTEPermission = useReselector(
    selectUserHasPermission,
    ProductFeatureId.quarterlyTaxes
  )
  const hasAnnualTaxesPermission = useReselector(
    selectUserHasPermission,
    ProductFeatureId.annualTaxes
  )
  const isCanceledAccount = useReselector(isCanceled)
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)

  const financialAdvisoryPilotEnabled = userHasFinancialAdvisoryAccess()

  const financialAdvisoryProfile = useReselector(selectFinancialAdvisoryProfile)

  const advisoryPages = useMemo(() => {
    if (isAdmin) {
      return []
    }
    if (
      financialAdvisoryProfile &&
      (financialAdvisoryProfile?.status ===
        FinancialAdvisoryProfilesStatus.active ||
        financialAdvisoryProfile?.status ===
          FinancialAdvisoryProfilesStatus.plan_completed)
    ) {
      return [
        { end: true, label: 'Overview', to: '/advisory/overview' },
        { end: true, label: 'Goals', to: '/advisory/goals' },
        { end: true, label: 'Message Advisor', to: '/advisory/messages' },
      ]
    } else if (
      financialAdvisoryProfile &&
      financialAdvisoryProfile?.status ===
        FinancialAdvisoryProfilesStatus.onboarding
    ) {
      return [
        { end: true, label: 'Onboard', to: '/advisory/overview' },
        { end: true, label: 'Message Advisor', to: '/advisory/messages' },
      ]
    } else {
      return [{ end: true, label: 'Enroll', to: '/advisory/overview' }]
    }
  }, [isAdmin, financialAdvisoryProfile])

  if (!isAdmin && annualTaxFormsLoading) {
    return null
  }

  if (isAdmin) {
    return getConfigWithPermissions({
      overview: {
        end: true,
        icon: faHome,
        label: 'Dashboard',
        to: '/admin/finances',
      },
      allUsers: {
        end: true,
        icon: faUserMagnifyingGlass,
        label: 'All Users',
        to: '/admin/finances/list-users',
      },
      users: {
        icon: faUsers,
        label: 'Users',
        pages: filterNulls([
          {
            end: true,
            label: 'All Internal Users',
            to: '/admin/finances/all-internal-users',
          },
          {
            end: true,
            label: 'All Payroll Users',
            to: '/admin/payroll/users',
          },
          {
            end: true,
            label: 'Trigger SendGrid Emails',
            to: '/admin/finances/email-users',
          },
          MASS_MESSAGE_ADMIN(user)
            ? {
                end: true,
                label: 'Zendesk Bulk Messaging',
                to: '/admin/finances/mass-messaging',
              }
            : undefined,
          QA_PAGE_ENABLED()
            ? {
                end: true,
                label: 'QA Users',
                to: '/admin/finances/qa',
              }
            : undefined,
        ]),
      },
      taxes: {
        icon: faUniversity,
        label: 'Taxes',
        pages: [
          {
            end: true,
            label: 'Annual Tax Filings',
            to: '/admin/finances/annual-tax-filings',
          },
          {
            end: true,
            label: 'QTE Calculations',
            to: '/admin/finances/qte-calculations',
          },
          {
            end: true,
            label: 'Federal Income Tax Details',
            to: '/admin/finances/federal-income-tax',
          },
          {
            end: true,
            label: 'State Income Tax Details',
            to: '/admin/finances/state-income-tax',
          },
          {
            end: true,
            label: 'Tax Checklist',
            to: '/admin/finances/tax-checklist',
          },
        ],
      },
      books: {
        icon: faBook,
        label: 'Books',
        pages: [
          {
            end: true,
            label: 'Bookkeeping CRM',
            to: '/admin/finances/books/crm',
          },
          {
            end: true,
            label: 'Incomplete Books',
            to: '/admin/finances/books/incomplete',
          },
          { end: true, label: 'All Books', to: '/admin/finances/books' },
        ],
      },
      businessRules: {
        icon: faSuitcase,
        label: 'Business Rules',
        pages: [
          {
            end: true,
            label: 'Autocategorization Rules',
            to: '/admin/finances/transaction-rules',
          },
          {
            end: true,
            label: 'Chart of Accounts',
            to: '/admin/finances/transaction-categories',
          },
          {
            end: true,
            label: 'Action Items',
            to: '/admin/finances/all-action-items',
          },
          {
            end: true,
            label: 'User Document Categories',
            to: '/admin/finances/user-document-categories',
          },
          {
            end: true,
            label: 'Quarterly Tax Estimate Details',
            to: '/admin/finances/quarterly-tax-estimate-details',
          },
          {
            end: true,
            label: 'Annual Tax Details',
            to: '/admin/finances/annual-tax-details',
          },
        ],
      },
      profile: {
        icon: faUser,
        label: 'Profile',
        pages: [
          { end: true, label: 'Your Admin Profile', to: '/admin/profile' },
        ],
      },
    })
  }

  /**
   * For Users who have a canceled plan — we do not show anything outside of Homepage
   */
  if (isCanceledAccount) {
    return getConfigWithPermissions({
      home: {
        end: true,
        exact: true,
        icon: faHome,
        label: 'Home',
        to: '/accounts/canceled',
        match: '/',
      },
    })
  }

  const resourcesConfig = {
    icon: faQuestionCircle,
    label: 'Resources',
    pages: [
      {
        to: 'https://support.joinheard.com/',
        label: 'Help Center',
        newPage: true,
      },
      {
        to: 'https://www.joinheard.com/resources',
        label: 'Resource Hub',
        newPage: true,
      },
    ],
  }

  // Suppressed routes drop the open params, so the entry has to leave the route there.
  const isHiddenRoute = isAdviserHiddenRoute(`${pathname}${search}`)
  const havenSearch = new URLSearchParams(isHiddenRoute ? '' : search)
  havenSearch.set(OPEN_HAVEN_QUERY_PARAM, 'true')
  // Pilot customers have no Zendesk threads, so their entry skips the open-tickets launch.
  if (!havenHandToSupportEnabled)
    havenSearch.set(HAVEN_CONTEXT_QUERY_PARAM, HAVEN_LAUNCH_ACTION.openTickets)
  havenSearch.set(HAVEN_ENTRY_QUERY_PARAM, HAVEN_ENTRY_POINT.sidebarMessages)
  if (havenHandToSupportEnabled)
    havenSearch.set(
      HAVEN_VIEW_QUERY_PARAM,
      messageCount > 0 ? RAIL_FILTERS.needsAttention : RAIL_FILTERS.all
    )
  const havenMessagesTarget = isHiddenRoute
    ? `/dashboard?${havenSearch.toString()}`
    : `?${havenSearch.toString()}`

  const messagesConfig = {
    end: true,
    icon: faEnvelope,
    label: 'Messages',
    to:
      havenMessagesViewEnabled || havenHandToSupportEnabled
        ? havenMessagesTarget
        : defaultMessagesURL,
    ornament: <NavigationCountIconRestructured count={messageCount} />,
    disabled: !userEmailVerified,
    tooltip: !userEmailVerified
      ? 'Please verify your email to access Messages.'
      : undefined,
  }

  if (isNil(user?.onboardingItemsCompletedAt)) {
    return getConfigWithPermissions({
      onboarding: {
        end: true,
        icon: faCompass,
        label: 'Get Started with Heard',
        to: '/onboarding-v2',
      },
      messages: messagesConfig,
      resources: resourcesConfig,
    })
  }

  const restructuredTaxesPages: SidebarConfigProps['pages'] = [
    {
      label: 'Quarterly Taxes',
      to: '/taxes/quarterly',
      end: true,
      icon: !hasQTEPermission ? faLock : undefined,
    },
    {
      label: 'Annual Taxes',
      to: '/taxes/annual',
      icon: !hasAnnualTaxesPermission ? faLock : undefined,
    },
  ]

  if (hasAnnualTaxesPermission) {
    restructuredTaxesPages.push({
      label: 'Tax Return Archive',
      to: '/taxes/archive',
    })
  }

  const reportsPages: SidebarConfigProps['pages'] = [
    { label: 'Profit & Loss', to: '/reports/profit-loss/' },
    { end: true, label: 'Practice Insights', to: '/insights/overview' },
    { label: 'Annual Reports', to: '/reports/yearly' },
    {
      label: 'Monthly Reports',
      match: '/reports/bookkeeping',
      to: '/reports/bookkeeping/overview',
    },
  ]

  const toolsPages: SidebarConfigProps['pages'] = filterNulls([
    { end: true, label: 'Allocation Guides', to: '/allocation-guides' },
    { end: true, label: 'Goal Setting', to: '/goal-setting' },
    designSystem2Enabled
      ? {
          label: 'Income Planning',
          to: '/income-planning',
          isBetaFeature: true,
        }
      : null,
  ])

  // Banking and payroll are "active" when enrolled; upsell/enrollment states go below Resources
  const isPayrollActive = Boolean(
    payrollNavConfig.payroll && 'pages' in payrollNavConfig.payroll
  )
  // Same nav item; only the positioning (below Messages vs below Resources) differs.
  const bankingNavItem = {
    icon: faPiggyBank,
    label: 'Heard Banking',
    isNewFeature: true,
    to: '/banking',
  }
  const activeBankingConfig: SidebarConfig = hasBankingProfile
    ? { banking: bankingNavItem }
    : {}
  const upsellBankingConfig: SidebarConfig = !hasBankingProfile
    ? { banking: bankingNavItem }
    : {}
  const activePayrollConfig: SidebarConfig = isPayrollActive
    ? payrollNavConfig
    : {}
  const upsellPayrollConfig: SidebarConfig = !isPayrollActive
    ? payrollNavConfig
    : {}

  const advisoryConfig: SidebarConfig =
    financialAdvisoryPilotEnabled && !isInUnservicedFreeTrial
      ? {
          advisory: {
            icon: faHandHoldingCircleDollar,
            label: 'Financial Advisory',
            pages: advisoryPages,
            topRoute: '/advisory',
          },
        }
      : {}

  const referralsConfig: SidebarConfig = showReferralsLink
    ? {
        referrals: {
          icon: faGift,
          label: `Rewards - Get $${process.env.VITE_REFERRAL_FACTORY_REWARD_DOLLARS_SENDER_TOTAL}`,
          to: '/rewards',
        },
      }
    : {}

  // Spacer separates the Resources group from the utility items (banking/payroll upsell, rewards) below it
  const hasBelowResourcesItems =
    Object.keys(upsellBankingConfig).length > 0 ||
    Object.keys(upsellPayrollConfig).length > 0 ||
    Object.keys(referralsConfig).length > 0
  const spacerConfig: SidebarConfig = hasBelowResourcesItems
    ? { spacer: { isSpacer: true, label: '', to: '' } }
    : {}

  return getConfigWithPermissions({
    home: {
      end: true,
      exact: true,
      icon: faHome,
      label: 'Home',
      to: '/dashboard',
      match: ['/', '/onboarding-dashboard'],
    },
    transactions: {
      end: true,
      icon: faMoneyBillTransfer,
      label: 'Transactions',
      to: '/transactions',
    },
    documents: {
      end: true,
      icon: faFile,
      label: 'Documents',
      to: '/practice/documents',
    },
    ...(!isInUnservicedFreeTrial && {
      messages: {
        ...messagesConfig,
        icon: faComment,
      },
    }),
    ...activeBankingConfig,
    ...activePayrollConfig,
    reports: {
      icon: faChartMixed,
      label: 'Reports',
      pages: reportsPages,
    },
    taxes: {
      icon: faBank,
      label: 'Taxes',
      pages: restructuredTaxesPages,
    },
    ...advisoryConfig,
    tools: {
      icon: faCalculator,
      label: 'Tools',
      pages: toolsPages,
    },
    resources: resourcesConfig,
    ...spacerConfig,
    ...upsellBankingConfig,
    ...upsellPayrollConfig,
    ...referralsConfig,
  })
}
